import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
  Divider,
  Typography,
  Card,
  CardContent,
  Stack,
  LinearProgress,
  useMediaQuery,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Sync as SyncIcon,
  Lock as LockIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import { DataGrid } from '@mui/x-data-grid';
import weddingEventService from '../../services/weddingEventService';
import DialogHeader from '../Common/DialogHeader';
import BudgetAnalytics from './BudgetAnalytics';
import { formatHuf } from '../../utils/currency';

// ── Budget item form dialog (add + edit) ──────────────────────
function BudgetItemFormDialog({ open, title, formData, onChange, onClose, onSubmit, submitLabel, vendorCategories, isMobile }) {
  const set = (field) => (e) => onChange({ ...formData, [field]: e.target.value });

  const tervezett = parseFloat(formData.budgetedAmount) || 0;
  const valos     = parseFloat(formData.actualAmount)   || 0;
  const fizetve   = parseFloat(formData.paidAmount)     || 0;
  const diff      = valos - tervezett;
  const fizetettPct = tervezett > 0 ? Math.min((fizetve / tervezett) * 100, 100) : 0;
  const showPreview = tervezett > 0 || valos > 0 || fizetve > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ p: 0 }}>
        <DialogHeader
          icon={title?.toLowerCase().includes('szerkeszt') ? <EditIcon /> : <AddIcon />}
          title={title}
          onClose={onClose}
        />
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        <Stack spacing={2.5} sx={{ mt: 1 }}>

          {/* ── Besorolás ── */}
          <Box>
            <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1, lineHeight: 1 }}>
              Besorolás
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel>Kategória</InputLabel>
              <Select
                value={formData.vendorCategoryId}
                onChange={set('vendorCategoryId')}
                label="Kategória"
              >
                <MenuItem value=""><em>Kategória nélkül</em></MenuItem>
                {vendorCategories.map((vc) => (
                  <MenuItem key={vc.id} value={vc.id}>{vc.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Divider />

          {/* ── Leírás ── */}
          <Box>
            <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1.5, lineHeight: 1 }}>
              Tétel leírása
            </Typography>
            <TextField
              label="Leírás *"
              size="small"
              fullWidth
              value={formData.description}
              onChange={set('description')}
              placeholder="pl. Esküvői torta"
            />
          </Box>

          <Divider />

          {/* ── Összegek ── */}
          <Box>
            <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1.5, lineHeight: 1 }}>
              Összegek
            </Typography>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Tervezett *"
                  type="number"
                  size="small"
                  fullWidth
                  value={formData.budgetedAmount}
                  onChange={set('budgetedAmount')}
                  slotProps={{ input: { endAdornment: <InputAdornment position="end">Ft</InputAdornment> }, htmlInput: { min: 0, step: 1000 } }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Valós"
                  type="number"
                  size="small"
                  fullWidth
                  value={formData.actualAmount}
                  onChange={set('actualAmount')}
                  slotProps={{ input: { endAdornment: <InputAdornment position="end">Ft</InputAdornment> }, htmlInput: { min: 0, step: 1000 } }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Tooltip title="Mennyit fizettél már ki ebből a tételből">
                  <TextField
                    label="Fizetve"
                    type="number"
                    size="small"
                    fullWidth
                    value={formData.paidAmount}
                    onChange={set('paidAmount')}
                    slotProps={{ input: { endAdornment: <InputAdornment position="end">Ft</InputAdornment> }, htmlInput: { min: 0, step: 1000 } }}
                  />
                </Tooltip>
              </Grid>
            </Grid>

            {/* Live preview */}
            {showPreview && (
              <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Fizetve: {tervezett > 0 ? `${Math.round(fizetettPct)}%` : '—'}
                  </Typography>
                  {tervezett > 0 && (
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      sx={{ color: diff <= 0 ? 'success.main' : 'error.main' }}
                    >
                      {diff > 0
                        ? `+${parseFloat(diff).toLocaleString('hu-HU')} Ft túllépés`
                        : diff < 0
                        ? `${Math.abs(diff).toLocaleString('hu-HU')} Ft megtakarítás`
                        : 'Egyenlő'}
                    </Typography>
                  )}
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={fizetettPct}
                  sx={{
                    height: 4,
                    borderRadius: 2,
                    bgcolor: 'grey.200',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: fizetve > tervezett ? 'error.main' : fizetve > 0 ? 'success.main' : 'grey.400',
                      borderRadius: 2,
                    },
                  }}
                />
              </Box>
            )}
          </Box>

        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2.5, gap: 1, borderTop: '1px solid', borderTopColor: 'divider' }}>
        <Button onClick={onClose}>Mégse</Button>
        <Button
          variant="contained"
          onClick={onSubmit}
          disabled={!formData.description.trim() || !formData.budgetedAmount}
        >
          {submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function BudgetTab({ event, onRefresh }) {
  const budgetLimit = Number(event?.totalBudget) || 0;
  // Mobil tételkártyák 3 oszlopos összegei: ne törjön külön sorba a „Ft"
  const amountSx = { whiteSpace: 'nowrap', fontSize: { xs: '0.8125rem', sm: '0.875rem' } };
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const cardSx = {
    borderRadius: '16px',
    border: `1px solid ${theme.palette.divider}`,
    background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.secondary.light, 0.4)} 100%)`,
    boxShadow: `0 1px 3px ${alpha(theme.palette.primary.dark, 0.06)}, 0 4px 16px ${alpha(theme.palette.primary.dark, 0.04)}`,
    height: '100%',
  };
  const cardContentSx = { p: 2.5, '&:last-child': { pb: 2.5 } };
  const statsRowSx = {
    display: { xs: 'grid', sm: 'flex' },
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: { xs: 1.5, sm: 2 },
    mb: { xs: 2, sm: 3 },
    overflowX: { sm: 'auto' },
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': { display: 'none' },
    pb: 0.5,
  };
  const statItemSx = { flex: '1 1 0', minWidth: { xs: 0, sm: 160 } };

  const [budgetItems, setBudgetItems] = useState([]);
  const [budgetSummary, setBudgetSummary] = useState(null);
  const [vendorCategories, setVendorCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState({
    vendorCategoryId: '',
    description: '',
    budgetedAmount: '',
    actualAmount: '',
    paidAmount: '',
  });

  useEffect(() => {
    fetchBudgetData();
  }, [event.id]);

  const fetchBudgetData = async () => {
    try {
      setLoading(true);
      const [itemsResponse, summaryResponse, catResponse] = await Promise.all([
        weddingEventService.getBudgetItems(event.id),
        weddingEventService.getBudgetSummary(event.id),
        weddingEventService.getVendorCategories(event.id),
      ]);

      if (itemsResponse.success) {
        setBudgetItems(itemsResponse.data);
      }
      if (summaryResponse.success) {
        setBudgetSummary(summaryResponse.data);
      }
      if (catResponse.success) {
        setVendorCategories(catResponse.data);
      }
    } catch (err) {
      console.error('Fetch budget error:', err);
      setError('Hiba történt a költségvetés betöltése során.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddBudgetItem = async () => {
    try {
      await weddingEventService.addBudgetItem(event.id, {
        vendorCategoryId: formData.vendorCategoryId ? parseInt(formData.vendorCategoryId) : null,
        description: formData.description,
        budgetedAmount: parseFloat(formData.budgetedAmount),
        actualAmount: formData.actualAmount ? parseFloat(formData.actualAmount) : null,
        paidAmount: formData.paidAmount ? parseFloat(formData.paidAmount) : 0,
      });

      setAddDialogOpen(false);
      resetForm();
      fetchBudgetData();
      onRefresh();
    } catch (err) {
      console.error('Add budget item error:', err);
      alert('Hiba történt a tétel hozzáadása során.');
    }
  };

  const handleEditBudgetItem = async () => {
    try {
      await weddingEventService.updateBudgetItem(
        event.id,
        selectedItem.id,
        {
          vendorCategoryId: formData.vendorCategoryId ? parseInt(formData.vendorCategoryId) : null,
          description: formData.description,
          budgetedAmount: parseFloat(formData.budgetedAmount),
          actualAmount: formData.actualAmount ? parseFloat(formData.actualAmount) : null,
          paidAmount: formData.paidAmount ? parseFloat(formData.paidAmount) : 0,
        }
      );

      setEditDialogOpen(false);
      resetForm();
      setSelectedItem(null);
      fetchBudgetData();
      onRefresh();
    } catch (err) {
      console.error('Edit budget item error:', err);
      alert(err.response?.data?.error || 'Hiba történt a tétel frissítése során.');
    }
  };

  const handleDeleteBudgetItem = async (item) => {
    if (item.isAutoSynced) {
      alert('Az automatikusan szinkronizált tételek nem törölhetők. A kapcsolódó foglalást kell törölni.');
      return;
    }

    if (!window.confirm(`Biztosan törölni szeretnéd: ${item.description}?`)) {
      return;
    }

    try {
      await weddingEventService.deleteBudgetItem(event.id, item.id);
      fetchBudgetData();
      onRefresh();
    } catch (err) {
      console.error('Delete budget item error:', err);
      alert('Hiba történt a tétel törlése során.');
    }
  };

  const openEditDialog = (item) => {
    if (item.isAutoSynced) {
      alert('Az automatikusan szinkronizált tételek nem szerkeszthetők. A kapcsolódó foglalást kell módosítani.');
      return;
    }

    setSelectedItem(item);
    setFormData({
      vendorCategoryId: item.vendorCategoryId || item.customVendor?.vendorCategoryId || '',
      description: item.description,
      budgetedAmount: item.budgetedAmount,
      actualAmount: item.actualAmount || '',
      paidAmount: item.paidAmount || 0,
    });
    setEditDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      vendorCategoryId: '',
      description: '',
      budgetedAmount: '',
      actualAmount: '',
      paidAmount: '',
    });
  };

  const columns = [
    {
      field: 'isAutoSynced',
      headerName: '',
      width: 50,
      renderCell: (params) =>
        params.value ? (
          <LockIcon fontSize="small" color="disabled" titleAccess="Automatikusan szinkronizált" />
        ) : null,
    },
    {
      field: 'category',
      headerName: 'Kategória',
      flex: 1.5,
      minWidth: 220,
      renderCell: (params) => (
        // Egy sorban marad: hosszú név esetén ellipszis + tooltip, különben belelógna a következő sorba
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'nowrap', minWidth: 0, width: '100%' }}>
          <Typography variant="body2" noWrap title={params.row?.category?.name || 'Egyéb'} sx={{ minWidth: 64, flex: '1 1 auto' }}>
            {params.row?.category?.name || 'Egyéb'}
          </Typography>
          {params.row?.vendorCategory && (
            <Chip
              label={params.row.vendorCategory.name}
              title={params.row.vendorCategory.name}
              size="small"
              sx={{ flex: '0 1 auto', minWidth: 56, maxWidth: '55%', bgcolor: 'rgba(237,190,95,0.22)', color: '#7a560f', fontWeight: 600 }}
            />
          )}
        </Box>
      ),
    },
    {
      field: 'description',
      headerName: 'Leírás',
      flex: 2,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'nowrap', minWidth: 0, width: '100%', overflow: 'hidden' }}>
          <Typography variant="body2" noWrap title={params.value} sx={{ minWidth: 0, flexShrink: 1 }}>{params.value}</Typography>
          {params.row?.booking && (
            <Chip label="Piactér" size="small" color="primary" variant="outlined" sx={{ flexShrink: 0 }} />
          )}
          {params.row?.customVendor && (
            <Chip label="Egyedi" size="small" variant="outlined" sx={{ flexShrink: 0 }} />
          )}
        </Box>
      ),
    },
    {
      field: 'budgetedAmount',
      headerName: 'Tervezett',
      width: 130,
      renderCell: (params) => {
        const value = params.row?.budgetedAmount;
        if (!value) return '-';
        return `${parseFloat(value).toLocaleString('hu-HU')} Ft`;
      },
    },
    {
      field: 'actualAmount',
      headerName: 'Valós költség',
      width: 130,
      renderCell: (params) => {
        return formatHuf(params.row?.actualAmount);
      },
    },
    {
      field: 'paidAmount',
      headerName: 'Fizetve',
      width: 130,
      renderCell: (params) => {
        const paid = parseFloat(params.row?.paidAmount || 0);
        const budgeted = parseFloat(params.row?.budgetedAmount || 0);
        // A valós ár (ha már ismert) a viszonyítási alap — teljes kifizetésnél így 100%, nem 106%
        const hasActual = params.row?.actualAmount !== null && params.row?.actualAmount !== undefined && params.row?.actualAmount !== '';
        const base = hasActual ? parseFloat(params.row.actualAmount) : budgeted;
        const percentage = base > 0 ? (paid / base) * 100 : 0;

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body2">{formatHuf(paid)}</Typography>
            {percentage > 0 && (
              <Typography variant="caption" color="text.secondary">({percentage.toFixed(0)}%)</Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: 'remaining',
      headerName: 'Hátralék',
      width: 130,
      renderCell: (params) => {
        const budgeted = parseFloat(params.row?.budgetedAmount || 0);
        const hasActual = params.row?.actualAmount !== null && params.row?.actualAmount !== undefined && params.row?.actualAmount !== '';
        const effective = hasActual ? parseFloat(params.row.actualAmount) : budgeted;
        const paid = parseFloat(params.row?.paidAmount || 0);
        const remaining = effective - paid;

        return (
          <Typography
            variant="body2"
            color={remaining > 0 ? 'error.main' : 'success.main'}
          >
            {formatHuf(remaining)}
          </Typography>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Műveletek',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box>
          {!params.row?.isAutoSynced && (
            <>
              <IconButton
                size="small"
                onClick={() => openEditDialog(params.row)}
                title="Szerkesztés"
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => handleDeleteBudgetItem(params.row)}
                color="error"
                title="Törlés"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </>
          )}
          {params.row?.isAutoSynced && (
            <Chip
              label="Auto"
              size="small"
              icon={<SyncIcon />}
              variant="outlined"
              color="primary"
            />
          )}
        </Box>
      ),
    },
  ];
  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Budget Summary Cards */}
      <Box sx={statsRowSx}>
        <Box sx={statItemSx}>
          <Card sx={cardSx}>
            <CardContent sx={cardContentSx}>
              <Typography variant="body2" color="text.secondary" gutterBottom>Tervezett összesen</Typography>
              <Typography variant="h5" fontWeight={700} color="primary.dark" sx={{ lineHeight: 1.2 }}>
                {formatHuf(budgetSummary?.totalBudgeted || 0)}
              </Typography>
              {/* A tételek összege ≠ az eseménynél megadott keret — mindkettőt megmutatjuk */}
              {budgetLimit > 0 && (
                <Typography
                  variant="caption"
                  color={budgetLimit - (budgetSummary?.totalBudgeted || 0) < 0 ? 'error.main' : 'text.secondary'}
                  sx={{ display: 'block', mt: 0.5 }}
                >
                  Keret: {formatHuf(budgetLimit)}
                  {' · '}
                  {budgetLimit - (budgetSummary?.totalBudgeted || 0) >= 0
                    ? `${formatHuf(budgetLimit - (budgetSummary?.totalBudgeted || 0))} szabad`
                    : `${formatHuf((budgetSummary?.totalBudgeted || 0) - budgetLimit)} túllépés`}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Box>

        <Box sx={statItemSx}>
          <Card sx={cardSx}>
            <CardContent sx={cardContentSx}>
              <Typography variant="body2" color="text.secondary" gutterBottom>Valós költség</Typography>
              <Typography variant="h5" fontWeight={700} color="primary.dark" sx={{ lineHeight: 1.2 }}>
                {budgetSummary?.totalActual?.toLocaleString('hu-HU') || 0} Ft
              </Typography>
              {!!budgetSummary?.overUnder && (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: 0.5 }}>
                  {budgetSummary.overUnder > 0
                    ? <TrendingUpIcon fontSize="small" color="error" />
                    : <TrendingDownIcon fontSize="small" color="success" />}
                  <Typography variant="caption" color={budgetSummary.overUnder > 0 ? 'error.main' : 'success.main'}>
                    {budgetSummary.overUnder > 0 ? '+' : '−'}{formatHuf(Math.abs(budgetSummary.overUnder))} a terv{budgetSummary.overUnder > 0 ? ' felett' : ' alatt'}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>

        <Box sx={statItemSx}>
          <Card sx={cardSx}>
            <CardContent sx={cardContentSx}>
              <Typography variant="body2" color="text.secondary" gutterBottom>Fizetve</Typography>
              <Typography variant="h5" fontWeight={700} color="primary.dark" sx={{ lineHeight: 1.2 }}>
                {budgetSummary?.totalPaid?.toLocaleString('hu-HU') || 0} Ft
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {budgetSummary?.totalBudgeted > 0
                  ? `${((budgetSummary.totalPaid / budgetSummary.totalBudgeted) * 100).toFixed(0)}%`
                  : '0%'}
              </Typography>
            </CardContent>
          </Card>
        </Box>

        <Box sx={statItemSx}>
          <Card sx={cardSx}>
            <CardContent sx={cardContentSx}>
              <Typography variant="body2" color="text.secondary" gutterBottom>Hátralék</Typography>
              <Typography variant="h5" fontWeight={700} color="primary.dark" sx={{ lineHeight: 1.2 }}>
                {budgetSummary?.remaining?.toLocaleString('hu-HU') || 0} Ft
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Budget Analytics */}
      {budgetItems.length > 0 && (
        <BudgetAnalytics
          budgetItems={budgetItems}
          vendorCategories={vendorCategories}
          budgetSummary={budgetSummary}
          isMobile={isMobile}
          theme={theme}
        />
      )}

      {/* Header Actions */}
      <Box sx={{ mb: 3, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 2 }}>
        <Alert severity="info" sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SyncIcon sx={{ mr: 1 }} />
            A piactérről foglalt szolgáltatók automatikusan szinkronizálódnak. Hozzáadhatsz kézi tételeket is.
          </Box>
        </Alert>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddDialogOpen(true)}
          sx={{ flexShrink: 0 }}
        >
          Tétel Hozzáadása
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Mobil kártyalista / DataGrid */}
      {isMobile ? (
        <Stack spacing={1.5}>
          {budgetItems.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              Nincs még költségvetési tétel
            </Typography>
          )}
          {budgetItems.map(item => {
            const budgeted = parseFloat(item.budgetedAmount || 0);
            const hasActual = item.actualAmount !== null && item.actualAmount !== undefined && item.actualAmount !== '';
            const effective = hasActual ? parseFloat(item.actualAmount) : budgeted;
            const paid = parseFloat(item.paidAmount || 0);
            const remaining = effective - paid;
            const paidRatio = effective > 0 ? Math.min((paid / effective) * 100, 100) : 0;
            return (
              <Card key={item.id} sx={{ borderRadius: '12px', border: `1px solid ${theme.palette.divider}`, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  {/* Sor 1: Lock + Kategória */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {item.isAutoSynced && <LockIcon sx={{ fontSize: 14, color: 'text.disabled' }} />}
                      {item.booking && <Chip label="Piactér" size="small" color="primary" variant="outlined" />}
                      {item.customVendor && <Chip label="Egyedi" size="small" variant="outlined" />}
                    </Box>
                    {item.category && <Chip label={item.category.name} size="small" variant="outlined" />}
                  </Box>
                  {/* Sor 2: Leírás */}
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.75 }}>{item.description}</Typography>
                  {/* Sor 3: Összegek */}
                  <Box sx={{ display: 'flex', gap: 1, mb: 0.75 }}>
                    <Box sx={{ flex: 1, textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary" display="block">Tervezett</Typography>
                      <Typography variant="body2" fontWeight={600} sx={amountSx}>{formatHuf(budgeted)}</Typography>
                    </Box>
                    <Box sx={{ flex: 1, textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary" display="block">Valós</Typography>
                      <Typography variant="body2" fontWeight={600} sx={amountSx}>{formatHuf(item.actualAmount)}</Typography>
                    </Box>
                    <Box sx={{ flex: 1, textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary" display="block">Fizetve</Typography>
                      <Typography variant="body2" fontWeight={600} sx={amountSx}>{formatHuf(paid)}</Typography>
                    </Box>
                  </Box>
                  {/* Sor 4: Progress */}
                  <LinearProgress variant="determinate" value={paidRatio} sx={{ height: 6, borderRadius: 3, mb: 0.5 }} />
                  {/* Sor 5: Hátralék */}
                  <Typography variant="caption" color={remaining > 0 ? 'error.main' : 'success.main'} sx={{ display: 'block', mb: 1 }}>
                    Hátralék: {remaining.toLocaleString('hu-HU')} Ft
                  </Typography>
                  {/* Sor 6: Műveletek */}
                  {item.isAutoSynced ? (
                    <Chip label="Auto szinkronizált" size="small" icon={<SyncIcon />} variant="outlined" color="primary" />
                  ) : (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button variant="outlined" size="small" startIcon={<EditIcon />} onClick={() => openEditDialog(item)} sx={{ flex: 1, fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                        Szerkesztés
                      </Button>
                      {/* Törlés: visszafogott ikon-gomb, hogy ne versenyezzen a Szerkesztéssel */}
                <Tooltip title="Törlés">
                  <IconButton
                    onClick={() => handleDeleteBudgetItem(item)}
                    aria-label="Törlés"
                    sx={{ width: 40, height: 40, flexShrink: 0, color: 'text.secondary', border: '1px solid', borderColor: 'divider', borderRadius: '8px', '&:hover': { color: 'error.main', borderColor: 'error.light' } }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                    </Box>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      ) : (
        <Box sx={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={budgetItems}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            localeText={{
              noRowsLabel: 'Nincs még költségvetési tétel',
              MuiTablePagination: { labelRowsPerPage: 'Sorok száma:' },
            }}
            sx={{
              // Saját elemmel rajzolt cellák (chip, Fizetve %, Hátralék) is függőlegesen középen legyenek,
              // ne a cella tetején — különben elcsúsznak a sima szöveges cellákhoz képest
              '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' },
              '& .MuiDataGrid-cell:focus': { outline: 'none' },
              '& .MuiDataGrid-row': { '&:hover': { backgroundColor: 'action.hover' } },
            }}
          />
        </Box>
      )}

      {/* Add / Edit Budget Item Dialogs */}
      <BudgetItemFormDialog
        open={addDialogOpen}
        title="Költségvetési Tétel Hozzáadása"
        formData={formData}
        onChange={setFormData}
        onClose={() => { setAddDialogOpen(false); resetForm(); }}
        onSubmit={handleAddBudgetItem}
        submitLabel="Hozzáadás"
        vendorCategories={vendorCategories}
        isMobile={isMobile}
      />
      <BudgetItemFormDialog
        open={editDialogOpen}
        title="Költségvetési Tétel Szerkesztése"
        formData={formData}
        onChange={setFormData}
        onClose={() => { setEditDialogOpen(false); setSelectedItem(null); resetForm(); }}
        onSubmit={handleEditBudgetItem}
        submitLabel="Mentés"
        vendorCategories={vendorCategories}
        isMobile={isMobile}
      />
    </Box>
  );
}

export default BudgetTab;